import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mixed, number, object, string } from 'yup';
import { ISupplier } from '../../shared';
import { StockMovementType } from '../enums';

export function useStockMovementSchema(suppliers: ISupplier[]) {
    const { t } = useTranslation();
    return useMemo(
        () =>
            object().shape({
                type: mixed<StockMovementType>().oneOf(Object.values(StockMovementType), t('required')).required(),
                productCode: string().required(),
                amount: number().typeError(t('invalidNumber')).required().min(0, t('amountGreaterThanOrEqualToZero')),
                supplierId: string()
                    .nullable()
                    .when('type', {
                        is: (value: StockMovementType) =>
                            value === StockMovementType.IN_STOCK || value === StockMovementType.DISTRIBUTION,
                        then: (schema) => schema.required(),
                    }),
                stockLocationId: string()
                    .nullable()
                    .when('type', {
                        is: (value: StockMovementType) =>
                            value === StockMovementType.IN_STOCK || value === StockMovementType.OUT_STOCK,
                        then: (schema) => schema.required(),
                    }),
                organisationId: string()
                    .nullable()
                    .when('type', {
                        is: (value: StockMovementType) =>
                            value === StockMovementType.OUT_STOCK || value === StockMovementType.DISTRIBUTION,
                        then: (schema) => schema.required(),
                    }),
                productName: string().optional(),
                batch: object()
                    .shape({
                        id: string().required(),
                        supplierId: string().required(),
                        supplierName: string().required(),
                        date: string().required(),
                        referenceNumber: string(),
                    })
                    .optional()
                    .nullable()
                    .test('batchRequired', t('required'), (value, context) => {
                        const supplier = suppliers.find(({ id }) => id === context.parent.supplierId);
                        if (supplier?.isBatchRequired) {
                            return !!value;
                        }
                        return true;
                    }),
            }),
        [t, suppliers],
    );
}
